import { ServicePageHeader_productizedService$key } from '#app/__generated__/relay/ServicePageHeader_productizedService.graphql.js';
import { Badges } from '#app/components/Badges/Badges.js';
import { ProBadge } from '#app/components/ProBadge/ProBadge.js';
import { useBadges } from '#app/hooks/useBadges.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { useLocation } from '#app/hooks/useLocation.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { useSafeUserReviews } from '#app/hooks/useSafeUserReviews.js';
import { formatName } from '#app/utilities/formatName.js';
import { ServicePageHeaderView } from './ServicePageHeaderView.js';
import { graphql, useFragment } from '@contra/relay';
import { getRoute } from '@contra/routes';
import { isTruthy } from '@contra/utilities/isTruthy';

type Props = {
  readonly productizedServiceRef: ServicePageHeader_productizedService$key;
  readonly variant?: 'checkout' | 'default';
};

export const ServicePageHeader = ({
  productizedServiceRef,
  variant = 'default',
}: Props) => {
  const service = useFragment<ServicePageHeader_productizedService$key>(
    graphql`
      fragment ServicePageHeader_productizedService on ProductizedService {
        id
        analyticsId
        slug
        title
        price {
          timeline
        }
        visitorCanEdit
        ...ServicePageShowImage_productizedService
        cover {
          ...ProjectCoverWrapper_portfolioProjectCover
        }
        userProfile {
          ...useBadges_userProfile
          analyticsId
          avatarImage {
            id
            uid
            url
          }
          firstName
          lastName
          displayUsername
          hasActivePortfolioSubscription
          ...useSafeUserReviews_userProfile
        }
      }
    `,
    productizedServiceRef,
  );

  const isMobile = useIsMobile();
  const navigation = useNavigation();
  const { pathname } = useLocation();
  const { badges, hasBadges } = useBadges({
    hiddenBadges: ['VERIFICATION', 'WALLET_ACTIVE', 'CATEGORY', 'RATE'],
    maxCount: 2,
    userProfileRef: service.userProfile,
  });

  const fullName = formatName(
    {
      firstName: service.userProfile.firstName,
      lastName: service.userProfile.lastName,
    },
    'fullName',
  );

  const hasProBadge = service.userProfile.hasActivePortfolioSubscription;

  const badgesToDisplay = [
    hasProBadge ? (
      <ProBadge
        key="1"
        size="sm"
      />
    ) : null,
    hasBadges ? (
      <Badges
        badges={badges}
        badgeSize="small"
        showFullLabel
      />
    ) : null,
  ].filter(isTruthy);

  const { averageRating, reviews } = useSafeUserReviews({
    userProfileRef: service.userProfile,
  });

  const testimonialAvatars = reviews
    .map(({ author }) => {
      if (!author.avatarImage) {
        return null;
      }

      return {
        id: author.avatarImage.id,
        uid: author.avatarImage.uid,
        url: author.avatarImage.url,
      };
    })
    .filter(isTruthy);

  const avatarImage = service.userProfile.avatarImage
    ? {
        id: service.userProfile.avatarImage.id,
        uid: service.userProfile.avatarImage.uid,
        url: service.userProfile.avatarImage.url,
      }
    : null;

  return (
    <ServicePageHeaderView
      avatarImage={avatarImage}
      averageRating={averageRating}
      badges={badgesToDisplay}
      canEdit={service.visitorCanEdit}
      displayUsername={service.userProfile.displayUsername}
      fullName={fullName}
      hasCover={Boolean(service.cover)}
      mode="normal"
      onClick={() => {
        if (isMobile && typeof document !== 'undefined') {
          document
            .querySelector('#user-recommendations')
            ?.scrollIntoView({ behavior: 'smooth' });
        } else {
          // this could be on the service page or on the checkout page
          // so need to reuse the current pathname
          navigation.push(`${pathname}?tab=recommendations`);
        }
      }}
      onEdit={() =>
        navigation.push(
          getRoute('/s/@slug/edit', { args: { slug: service.slug } }),
        )
      }
      productizedServiceRef={service}
      recommenderAvatars={testimonialAvatars}
      serviceAnalyticsId={service.analyticsId}
      slug={service.slug}
      timeline={service.price.timeline}
      title={service.title}
      userProfileAnalyticsId={service.userProfile.analyticsId}
      variant={variant}
    />
  );
};
